import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspaces } from "@/contexts/WorkspaceContext";
import api, { formatApiError } from "@/lib/api";
import { ArrowRight, Loader2, Sparkles, Users } from "lucide-react";
import { toast } from "sonner";
import Wordmark from "@/components/Wordmark";

// Two-phase signup view:
//   "form"          → render the form
//   "create-ws"     → user is signed in but didn't join via invite → ask
//                     them to name their first workspace
const INVITE_ERROR_COPY = {
  not_found: "We couldn't find that invite. Ask your teammate to send a fresh one.",
  expired: "This invite has expired. Ask your teammate to send a fresh one.",
  revoked: "This invite has been revoked. Ask your teammate to send a fresh one.",
  exhausted: "This invite has been used up. Ask your teammate to send a fresh one.",
  email_mismatch:
    "This invite was sent to a different email. You can keep your account — ask the workspace owner to re-invite this email.",
};

export default function SignupPage() {
  const { user, signup } = useAuth();
  const wsCtx = useWorkspaces();
  const refetch = wsCtx?.refetch;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite") || "";
  const targetChatId = searchParams.get("chat") || "";

  // Form fields
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [phase, setPhase] = useState("form");
  // Once we POST signup we OWN the navigation flow — the auto-bounce in
  // the render guard below would otherwise race AuthProvider's setUser
  // and yank the user to /all-chats before we can show the create-ws step.
  const submittedRef = useRef(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(Boolean(inviteToken));

  // Create-workspace fields (phase: create-ws)
  const [newWsName, setNewWsName] = useState("");
  const [creatingWs, setCreatingWs] = useState(false);

  // Look up invite info on mount
  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/invites/${inviteToken}`);
        if (!cancelled) {
          setInviteInfo(data);
          if (data && data.valid && data.invited_email) {
            setEmail(data.invited_email);
          }
        }
      } catch (_) {
        if (!cancelled) setInviteInfo({ valid: false, reason: "not_found" });
      } finally {
        if (!cancelled) setInviteLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  // Already-logged-in users bounce
  if (user === null) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-[#FAF9F7]">
        <p className="font-sans text-sm text-[#7A7571]">Loading…</p>
      </div>
    );
  }
  if (user && user.id && phase === "form" && !submittedRef.current) {
    // If you stumble onto /signup while signed in, just send you home.
    return <Navigate to="/" replace />;
  }

  const passwordOK = password.length >= 8;
  const matchesConfirm = password && password === confirm;
  const displayNameOK = displayName.trim().length >= 2;
  const emailOK = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  const formValid =
    displayNameOK && emailOK && passwordOK && matchesConfirm && !submitting;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!formValid) return;
    submittedRef.current = true;
    setSubmitting(true);
    setError("");
    const res = await signup({
      display_name: displayName.trim(),
      email: email.trim(),
      password,
      invite_token: inviteToken || undefined,
      target_chat_id: targetChatId || undefined,
    });
    setSubmitting(false);
    if (!res.ok) {
      // Roll back ownership of nav so we can be auto-bounced cleanly if the
      // user later logs in through another tab.
      submittedRef.current = false;
      setError(res.error || "Couldn't create your account");
      return;
    }
    const joined = res.data?.joined_workspace || null;
    const inviteError = res.data?.invite_error || null;
    const redirectTo = res.data?.redirect_to || null;
    if (joined && joined.slug) {
      if (refetch) await refetch();
      if (inviteError) {
        toast.warning(
          INVITE_ERROR_COPY[inviteError] || `Invite issue: ${inviteError}`
        );
      } else {
        toast.success(`Welcome to ${joined.name || "your workspace"}.`);
      }
      navigate(redirectTo || `/w/${joined.slug}`);
      return;
    }
    if (inviteError) {
      toast.warning(
        INVITE_ERROR_COPY[inviteError] || `Invite issue: ${inviteError}`
      );
    }
    setPhase("create-ws");
  };

  const createWorkspace = async (e) => {
    e?.preventDefault?.();
    const trimmed = newWsName.trim();
    if (!trimmed || creatingWs) return;
    setCreatingWs(true);
    try {
      const { data } = await api.post("/workspaces", { name: trimmed });
      if (refetch) await refetch();
      toast.success(`Workspace "${data.name}" created`);
      navigate(`/w/${data.slug}`);
    } catch (err) {
      toast.error(formatApiError(err));
      setCreatingWs(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full bg-[#F3F1EC] flex items-center justify-center px-6 py-10"
      data-testid="signup-page"
    >
      <Link
        to="/"
        className="absolute top-8 left-8 font-serif text-xl tracking-tight text-[#2C2A29] select-none hover:opacity-80 transition-opacity"
      >
        <Wordmark size="md" testid="signup-header-wordmark" />
      </Link>

      <div className="w-full max-w-md bg-[#FAF9F7] border border-[#DCD7CE] rounded-2xl shadow-sm px-8 py-10 sm:px-10 sm:py-12">
        {phase === "form" ? (
          <>
            <p className="font-sans text-xs uppercase tracking-[0.22em] text-[#7A7571] mb-3">
              Make an account
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-none text-[#2C2A29] mb-3">
              Step inside.
            </h1>

            {inviteToken ? (
              <InviteBanner
                inviteInfo={inviteInfo}
                loading={inviteLoading}
                token={inviteToken}
              />
            ) : (
              <p className="font-sans text-sm text-[#7A7571] mb-7 leading-relaxed">
                An onebrain account lets you start a workspace and bring your
                people in.
              </p>
            )}

            <form
              onSubmit={submit}
              className="space-y-5"
              data-testid="signup-form"
            >
              <Field
                label="Display name"
                id="display_name"
                value={displayName}
                onChange={setDisplayName}
                placeholder="e.g. Alex"
                autoComplete="name"
                dataTestid="signup-display-name-input"
              />
              <Field
                label="Email"
                id="email"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="you@somewhere.com"
                autoComplete="email"
                dataTestid="signup-email-input"
                hint={
                  inviteInfo?.valid && inviteInfo?.invited_email
                    ? "Pre-filled from your invite"
                    : ""
                }
              />
              <Field
                label="Password"
                id="password"
                type="password"
                value={password}
                onChange={setPassword}
                placeholder="At least 8 characters"
                autoComplete="new-password"
                dataTestid="signup-password-input"
                hint={
                  password
                    ? passwordOK
                      ? "Looks good"
                      : "8 characters minimum"
                    : ""
                }
                hintColor={
                  password ? (passwordOK ? "#3A5A40" : "#8C3A1F") : "#7A7571"
                }
              />
              <Field
                label="Confirm password"
                id="confirm_password"
                type="password"
                value={confirm}
                onChange={setConfirm}
                placeholder="Type it again"
                autoComplete="new-password"
                dataTestid="signup-confirm-password-input"
                hint={
                  confirm
                    ? matchesConfirm
                      ? "Matches"
                      : "These don't match yet"
                    : ""
                }
                hintColor={
                  confirm
                    ? matchesConfirm
                      ? "#3A5A40"
                      : "#8C3A1F"
                    : "#7A7571"
                }
              />

              {error ? (
                <div
                  data-testid="signup-error"
                  className="rounded-lg border border-[#D96C4A]/30 bg-[#D96C4A]/10 px-4 py-3 font-sans text-sm text-[#8C3A1F]"
                >
                  {error}
                </div>
              ) : null}

              <button
                type="submit"
                data-testid="signup-submit-button"
                disabled={!formValid}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#2C2A29] text-white font-sans text-sm font-medium tracking-wide hover:bg-[#1A1918] disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {submitting ? "Creating your account…" : "Create account"}
                {!submitting ? (
                  <ArrowRight size={14} strokeWidth={2} />
                ) : null}
              </button>
            </form>

            <p className="mt-8 font-sans text-sm text-[#7A7571]">
              Already have an account?{" "}
              <Link
                to="/login"
                className="text-[#2C2A29] underline underline-offset-2 hover:text-[#D96C4A] transition-colors"
                data-testid="signup-signin-link"
              >
                Sign in
              </Link>
            </p>
          </>
        ) : (
          <CreateFirstWorkspace
            value={newWsName}
            onChange={setNewWsName}
            onSubmit={createWorkspace}
            submitting={creatingWs}
          />
        )}
      </div>
    </div>
  );
}

function InviteBanner({ inviteInfo, loading, token }) {
  if (loading) {
    return (
      <div
        className="rounded-xl border border-[#DCD7CE] bg-white px-4 py-3 mb-7 flex items-center gap-2 font-sans text-sm text-[#7A7571]"
        data-testid="signup-invite-banner-loading"
      >
        <Loader2 className="animate-spin" size={14} /> Loading invite…
      </div>
    );
  }
  if (!inviteInfo || !inviteInfo.valid) {
    return (
      <div
        className="rounded-xl border border-[#D96C4A]/30 bg-[#D96C4A]/10 px-4 py-3 mb-7 font-sans text-sm text-[#8C3A1F]"
        data-testid="signup-invite-banner-invalid"
      >
        {INVITE_ERROR_COPY[inviteInfo?.reason] ||
          "We couldn't validate that invite. You can still create an account — your teammate can re-invite you."}
        <div className="text-xs text-[#A5A29E] mt-1 font-mono">
          token: {token.slice(0, 10)}…
        </div>
      </div>
    );
  }
  return (
    <div
      className="rounded-xl border border-[#3A5A40]/30 bg-[#3A5A40]/8 px-4 py-3 mb-7"
      data-testid="signup-invite-banner-valid"
    >
      <div className="flex items-center gap-2 mb-1">
        <Users size={13} className="text-[#3A5A40]" strokeWidth={1.8} />
        <span className="font-sans text-[10px] uppercase tracking-[0.18em] text-[#3A5A40] font-semibold">
          You're invited
        </span>
      </div>
      <p className="font-sans text-sm text-[#2C2A29] leading-relaxed">
        <span className="font-medium">
          {inviteInfo.inviter?.display_name || "Someone"}
        </span>{" "}
        invited you to join{" "}
        <span className="font-medium">
          {inviteInfo.workspace?.name || "a workspace"}
        </span>{" "}
        as <span className="font-medium">{inviteInfo.role || "member"}</span>.
      </p>
    </div>
  );
}

function CreateFirstWorkspace({ value, onChange, onSubmit, submitting }) {
  return (
    <>
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={16} className="text-[#D96C4A]" strokeWidth={1.8} />
        <span className="font-sans text-xs uppercase tracking-[0.22em] text-[#7A7571] font-semibold">
          You're in
        </span>
      </div>
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-none text-[#2C2A29] mb-3">
        Name your first
        <br />
        workspace.
      </h1>
      <p className="font-sans text-sm text-[#7A7571] mb-7 leading-relaxed">
        A workspace is a private place where you and a few people share chats
        with Claude.
      </p>
      <form
        onSubmit={onSubmit}
        className="space-y-5"
        data-testid="signup-create-ws-form"
      >
        <Field
          label="Workspace name"
          id="ws_name"
          value={value}
          onChange={onChange}
          placeholder="e.g. Acme Co. · just-me · The Book Club"
          autoFocus
          dataTestid="signup-create-ws-input"
        />
        <button
          type="submit"
          data-testid="signup-create-ws-submit"
          disabled={!value.trim() || submitting}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#2C2A29] text-white font-sans text-sm font-medium tracking-wide hover:bg-[#1A1918] disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {submitting ? "Creating…" : "Create workspace"}
          {!submitting ? <ArrowRight size={14} strokeWidth={2} /> : null}
        </button>
      </form>
    </>
  );
}

function Field({
  label,
  id,
  type = "text",
  value,
  onChange,
  placeholder,
  autoComplete,
  autoFocus,
  hint,
  hintColor,
  dataTestid,
}) {
  return (
    <div className="space-y-2">
      <label
        htmlFor={id}
        className="block font-sans text-xs uppercase tracking-widest text-[#7A7571] font-semibold"
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={dataTestid}
        className="w-full px-4 py-3 rounded-lg bg-white border border-[#DCD7CE] font-sans text-base text-[#2C2A29] placeholder:text-[#A5A29E] focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30 focus:border-[#D96C4A]/40 transition"
      />
      {hint ? (
        <p
          className="font-sans text-xs"
          style={{ color: hintColor || "#7A7571" }}
        >
          {hint}
        </p>
      ) : null}
    </div>
  );
}
